import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { FaEdit, FaTrash } from 'react-icons/fa';
import AddSupplierModal from '../components/AddSupplierModal';
import Pagination from '../components/Pagination';
import 'bootstrap/dist/css/bootstrap.min.css';
import { toast } from 'react-toastify';
import ConfirmModal from '../components/ConfirmModal';
import Loader from '../components/Loader';

const Taminlovchilar = () => {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  
  // State management
  const [suppliers, setSuppliers] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // Modal states
  const [showModal, setShowModal] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [supplierToEdit, setSupplierToEdit] = useState(null);
  
  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(20);
  
  // Delete confirmation
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [supplierToDelete, setSupplierToDelete] = useState(null);

  // Search input state
  const [search, setSearch] = useState('');

  // Fetch data function
  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({
        page: currentPage,
        limit: itemsPerPage
      });
      if (search && search.trim() !== '') params.append('search', search.trim());
      const response = await fetch(
        `/api/suppliers/search?${params.toString()}`,
        {
          headers: {
            ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
            'x-owner-id': String(user?.ownerId || user?.id || ''),
            'x-staff-id': String(user?.staffId || '')
          }
        }
      );
      const data = await response.json();
      
      console.log('Received suppliers data:', data);
      
      if (!response.ok) throw new Error(data.message || 'Failed to fetch suppliers');
      
      const items = Array.isArray(data) ? data : (Array.isArray(data.items) ? data.items : []);
      setSuppliers(items);
      setTotalCount(Number(data.total) || items.length);
    } catch (err) {
      setError(err.message);
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setCurrentPage(1); // Reset page when search or items per page changes
  }, [search, itemsPerPage]);
  useEffect(() => {
    fetchData();
  }, [currentPage, itemsPerPage, search]);

  // Handlers
  const handleAdd = () => {
    setIsEditMode(false);
    setSupplierToEdit(null);
    setShowModal(true);
  };

  const handleEdit = (supplier) => {
    setSupplierToEdit({
      ...supplier,
      supplierid: supplier.supplierid,
      createdat: supplier.createdat
    });
    setIsEditMode(true);
    setShowModal(true);
  };

  const handleDelete = (supplier) => {
    if (!supplier?.supplierid) {
      toast.error('Invalid supplier data');
      return;
    }
    setSupplierToDelete(supplier);
    setShowConfirmModal(true);
  };

  const confirmDelete = async () => {
    if (!supplierToDelete?.supplierid) {
      toast.error('Please select a valid supplier to delete');
      setShowConfirmModal(false);
      return;
    }
    
    try {
      const response = await fetch(`/api/suppliers/${supplierToDelete.supplierid}`, {
        method: 'DELETE',
        headers: {
          ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
          'Content-Type': 'application/json',
          'x-owner-id': String(user?.ownerId || user?.id || ''),
          'x-staff-id': String(user?.staffId || '')
        }
      });
      
      if (!response.ok) throw new Error('Delete failed');
      
      setSuppliers(prev => prev.filter(s => s.supplierid !== supplierToDelete.supplierid));
      toast.success('Supplier deleted successfully');
    } catch (err) {
      toast.error(err.message);
      console.error('Delete error:', err);
    } finally {
      setShowConfirmModal(false);
      setSupplierToDelete(null);
    }
  };

  const handleSubmit = async (formData) => {
    try {
      const url = isEditMode 
        ? `/api/suppliers/${supplierToEdit.supplierid}` 
        : '/api/suppliers';
      
      const method = isEditMode ? 'PUT' : 'POST';
      
      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
          'x-owner-id': String(user?.ownerId || user?.id || ''),
          'x-staff-id': String(user?.staffId || '')
        },
        body: JSON.stringify(formData)
      });

      if (!response.ok) throw new Error('Operation failed');
      
      const responseData = await response.json();
      
      // Update local state immediately
      if (isEditMode) {
        setSuppliers(prev => prev.map(s => 
          s.supplierid === supplierToEdit.supplierid ? {...s, ...formData} : s
        ));
      } else {
        const created = Array.isArray(responseData?.items) ? responseData.items[0] : responseData;
        setSuppliers(prev => [created, ...prev]);
      }
      
      toast.success(`Supplier ${isEditMode ? 'updated' : 'added'} successfully`);
      setShowModal(false);
    } catch (err) {
      toast.error(err.message);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) {
      console.warn('Missing date');
      return 'N/A';
    }
    
    try {
      // Handle all possible PostgreSQL timestamp formats
      let date;
      
      // Try ISO format first
      if (dateString.includes('T')) {
        date = new Date(dateString);
      } 
      // Handle PostgreSQL timestamp without T (2023-01-01 12:34:56)
      else if (/\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}/.test(dateString)) {
        date = new Date(dateString.replace(' ', 'T'));
      }
      // Handle locale-specific formats as fallback
      else {
        date = new Date(dateString);
      }
      
      if (isNaN(date.getTime())) {
        console.warn('Invalid date format:', dateString);
        return 'N/A';
      }
      
      // Format as DD-MM-YYYY, HH:MM
      const pad = num => String(num).padStart(2, '0');
      return `${pad(date.getDate())}-${pad(date.getMonth()+1)}-${date.getFullYear()}, ${pad(date.getHours())}:${pad(date.getMinutes())}`;
    } catch (err) {
      console.error('Date parsing error:', err);
      return 'N/A';
    }
  };

  return (
    <div className="container-fluid p-4">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2>Ta'minotchilar</h2>
        <input
          type="text"
          className="form-control w-25"
          style={{ maxWidth: 300 }}
          placeholder="Ta'minotchi nomi yoki telefoni bo'yicha qidiruv"
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
        <button className="btn btn-success" onClick={handleAdd}>
          Qo'shish
        </button>
      </div>
      {loading ? (
        <Loader />
      ) : error ? (
        <div className="alert alert-danger text-center">{error}</div>
      ) : suppliers && suppliers.length > 0 ? (
        <>
          
          <div className="table-responsive rounded-4 shadow">
            <table className="table table-striped mb-0">
            <thead>
              <tr>
                <th>#</th>
                <th>Nomi</th>
                <th>Telefon</th>
                <th>Mas'ul shaxs</th>
                <th>Valyuta</th>
                <th>Yaratilgan vaqt</th>
                <th>Harakatlar</th>
              </tr>
            </thead>
            <tbody>
              {suppliers.map((supplier, index) => (
                <tr key={supplier.supplierid}>
                  <td>{index + 1}</td>
                  <td>{supplier.name}</td>
                  <td>{supplier.phone}</td>
                  <td>{supplier.fio}</td>
                  <td>{supplier.currency || 'UZS'}</td>
                  <td>{formatDate(supplier.createdat)}</td>
                  <td>
                    <button
                      className="btn btn-success btn-sm me-2"
                      onClick={() => handleEdit(supplier)}
                    >
                      <FaEdit />
                    </button>
                    <button
                      className="btn btn-danger btn-sm"
                      onClick={() => handleDelete(supplier)}
                    >
                      <FaTrash />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
          <Pagination
            currentPage={currentPage}
            totalItems={totalCount}
            itemsPerPage={itemsPerPage}
            onPageChange={setCurrentPage}
            onItemsPerPageChange={setItemsPerPage}
          />
        </>
      ) : (
        <div className="alert alert-info text-center">Ma'lumot topilmadi</div>
      )}
      <AddSupplierModal
        show={showModal}
        onClose={() => {
          setShowModal(false);
          setIsEditMode(false);
          setSupplierToEdit(null);
        }}
        onSave={handleSubmit}
        isEditMode={isEditMode}
        supplierToEdit={supplierToEdit}
      />
      <ConfirmModal
        show={showConfirmModal}
        onConfirm={confirmDelete}
        onCancel={() => setShowConfirmModal(false)}
        message={`Ta'minotchini o'chirishga ishonchingiz komilmi?`}
      />
    </div>
  );
};

export default Taminlovchilar;